<template>
    <a-popup :visible="visible" @cancel="onCancel">
        <div class="tw-mx-auto tw-w-[95%] md:tw-max-w-[640px] tw-overflow-hidden tw-h-fit tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-shadow-md tw-my-5 tw-rounded">
            <div class="tw-p-4 tw-text-lg tw-border-b tw-border-solid tw-font-medium tw-text-gray-600 tw-bg-gray-50">
                {{ $t('portal.notifications.send') }}
            </div>

            <div class="tw-p-5 tw-bg-white">
                <div class="tw-grid tw-grid-cols-12 tw-gap-x-4">
                    <div class="tw-col-span-12">
                        <FormField v-model="form.title" :label="$t('portal.fields.title')" type="text" :error="err('title')" />
                    </div>
                    <div class="tw-col-span-12">
                        <FormField v-model="form.message" :label="$t('portal.fields.message')" type="textarea" :error="err('message')" />
                    </div>
                    <div class="tw-col-span-12" :class="form.recipient_type === 'selected' ? 'md:tw-col-span-5' : ''">
                        <FormField v-model="form.recipient_type" :label="$t('portal.fields.recipients')" type="select" :options="recipientOptions" :error="err('recipient_type')" />
                    </div>
                    <div class="tw-col-span-12 md:tw-col-span-7" v-if="form.recipient_type === 'selected'">
                        <FormVueSelect multiple v-model="form.customer_ids" option-value="id" option-label="name"
                            :label="$t('portal.fields.customers')" :options="customers" :error="err('customer_ids')" />
                    </div>
                </div>
            </div>

            <div class="tw-p-4 tw-border-t tw-justify-end tw-flex tw-items-center tw-gap-2 tw-bg-gray-50">
                <a-button :disabled="loading" @click="onCancel" intent="simple" class="tw-min-w-[120px]">{{ $t('buttons.cancel') }}</a-button>
                <a-button :disabled="loading" :loading="loading" left-icon="solar:plain-bold-duotone" @click="onSend" class="tw-min-w-[120px]">{{ $t('portal.notifications.send-button') }}</a-button>
            </div>
        </div>
    </a-popup>
</template>

<script setup>
import { ref, computed, watch, defineProps, defineEmits } from 'vue';
import { useI18n } from 'vue-i18n';
import FormField from '@/components/form/FormField';
import FormVueSelect from '@/components/form/FormVueSelect';
import { all as allCustomers } from '@/api/customers';
import { send } from '@/api/notifications';
import useAlert from '@/composables/useAlert';
import { useValidation } from '@/composables/useValidation';

const props = defineProps({ visible: { type: Boolean, default: false } });
const emit = defineEmits(['cancel', 'saved']);
const i18n = useI18n();

const loading = ref(false);
const errors = ref({});
const customers = ref([]);
const form = ref(defaults());
const err = useValidation(errors);

const recipientOptions = computed(() => [
    { value: 'all', label: i18n.t('portal.notifications.all-customers') },
    { value: 'selected', label: i18n.t('portal.notifications.selected-customers') },
]);

function defaults() {
    return { title: '', message: '', recipient_type: 'all', customer_ids: [] };
}

async function loadCustomers() {
    try {
        const { data } = await allCustomers();
        customers.value = (data.customers || []).map((c) => ({ id: c.id, name: `${c.name}${c.email ? ' · ' + c.email : ''}` }));
    } catch (e) { /* interceptor */ }
}

watch(() => props.visible, (v) => {
    if (v) {
        errors.value = {};
        form.value = defaults();
        if (!customers.value.length) loadCustomers();
    }
});

const onCancel = () => { if (!loading.value) emit('cancel'); };

const onSend = async () => {
    loading.value = true;
    errors.value = {};
    try {
        const { data } = await send(form.value);
        if (data.code === 'SUCCESS') {
            useAlert(i18n.t('portal.notifications.sent'));
            emit('saved', data.notification);
        }
    } catch (e) {
        if (e.response?.status === 422) {
            for (const k in e.response.data.errors) errors.value[k] = e.response.data.errors[k][0];
        }
    } finally {
        loading.value = false;
    }
};
</script>
